/** The published geometry behind a circuit, for a page that draws it.
 *
 * The compiled outline belongs to the track-data repo and the service passes
 * it through, so a page asks for it once it knows which circuit it is looking
 * at — the spectate page from the stream's `hello`, the Sessions page from the
 * session's layout id. A circuit nobody has published yet answers 404, which
 * is not an error here: the caller falls back to whatever it can draw without
 * one. Three states rather than two, because two would lie: `undefined` while
 * the answer is on its way, so a map does not announce "no outline" about a
 * circuit it has not asked about yet, and `null` once the answer is that
 * there is none.
 */

import { useEffect, useState } from 'react';

import { getTrack } from '@/lib/api';
import type { TrackGeometry } from '@/lib/types';

export function useTrackGeometry(officialId: string): TrackGeometry | null | undefined {
  const [geometry, setGeometry] = useState<TrackGeometry | null | undefined>(undefined);

  useEffect(() => {
    if (!officialId) {
      setGeometry(null);
      return;
    }
    let alive = true;
    setGeometry(undefined);
    void getTrack(officialId)
      .then((track) => {
        if (alive) setGeometry(track.geometry ?? null);
      })
      .catch(() => {
        if (alive) setGeometry(null);
      });
    return () => {
      alive = false;
    };
  }, [officialId]);

  return geometry;
}
